// src/redux/sagas/timeSlotsSaga.ts
import { call, put, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { request } from '../../api/request';
import ENDPOINTS from '../../api/endpoints';
import { getRestaurantId } from '../../api/tokenUtils';
import type { TimeSlotsRequest, TimeSlotsResponse } from '../../types/tableReservation';
import {
  fetchTimeSlotsRequest,
  fetchTimeSlotsSuccess,
  fetchTimeSlotsFailure,
} from '../slices/timeSlotsSlice';

/**
 * Saga to fetch time slots for a given day of the week.
 * 
 * @param action - Redux action with day parameter (e.g., "monday", "tuesday")
 */
function* fetchTimeSlotsSaga(
  action: PayloadAction<TimeSlotsRequest>
): Generator<unknown, void, AxiosResponse<TimeSlotsResponse>> {
  try {
    const { day } = action.payload;
    
    // Get restaurant ID
    const restaurantId = getRestaurantId();
    
    if (!restaurantId) {
      yield put(fetchTimeSlotsFailure('Restaurant ID not found'));
      return;
    }
    
    // API call to fetch time slots by day with restaurant-id header
    const response = yield call(request<TimeSlotsResponse>, {
      method: 'GET',
      url: `${ENDPOINTS.TABLE_TIME_SLOTS_API_PATH}/${day}`,
      base: ENDPOINTS.API_V2_BASE_URL,
      headers: {
        'restaurant-id': restaurantId,
      },
    });

    // Check if response is successful
    if (response.data.success && response.data.data) {
      yield put(fetchTimeSlotsSuccess(response.data.data));
    } else {
      const errorMsg =
        response.data.message || response.data.error || 'Failed to fetch time slots';
      yield put(fetchTimeSlotsFailure(errorMsg));
    }
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to fetch time slots';
    yield put(fetchTimeSlotsFailure(errorMessage));
  }
}

export function* watchTimeSlotsSaga() {
  yield takeEvery(fetchTimeSlotsRequest.type, fetchTimeSlotsSaga);
}
